import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Icon, Card, CardBody, showToast } from '@repo/ui';
import {
  useOAuthCallback,
  useMicrosoftCallback,
} from '@xtnd/web/features/auth/queries/useAuthQuery';
import { UserRole } from '@repo/types';
import { Platform } from '@xtnd/web/shared';

import { getDefaultRouterLoggedInCallback } from '../../permissions/permission';
import { authApi } from '../api';

export function ClioCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing your login...');

  const oauthCallbackMutation = useOAuthCallback();
  const microsoftCallbackMutation = useMicrosoftCallback();
  const handledRef = useRef(false);

  const currentPlatform = import.meta.env.VITE_PLATFORM || Platform.LYDECKER;

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    const handleCallback = () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const token = searchParams.get('token');
      const userParam = searchParams.get('user');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Handle OAuth errors
      if (error) {
        setStatus('error');
        const errorMsg = decodeURIComponent(error);
        const errorDesc = errorDescription ? decodeURIComponent(errorDescription) : undefined;
        setMessage(errorDesc || `Authentication failed: ${errorMsg}`);
        showToast.error(`Login failed: ${errorMsg}`);

        // Redirect to login after delay
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 5000);
        return;
      }

      // Handle Microsoft OAuth direct token callback
      if (token && userParam) {
        try {
          // Store the token first
          localStorage.setItem('accessToken', token);
          localStorage.setItem('idToken', token); // Use same token for both

          // Set expiry to 24 hours (matching our JWT expiry)
          const expiredAt = Date.now() + 24 * 60 * 60 * 1000;
          localStorage.setItem('expiredAt', expiredAt.toString());

          // Now call /auth/me to get enriched user info (same as Cognito flow)
          setMessage('Microsoft login successful! Getting user information...');

          // Use the Microsoft-specific hook to get enriched user data
          microsoftCallbackMutation.mutate(
            undefined, // No parameters needed, token already stored
            {
              onSuccess: () => {
                setStatus('success');
                setMessage('Login successful! Redirecting...');
                const role = authApi.getCurrentUserRole();
                const defaultRedirectRoute = getDefaultRouterLoggedInCallback(
                  currentPlatform as Platform,
                  role as UserRole
                );

                const savedRedirect = sessionStorage.getItem('auth_redirect_path');
                const destination = savedRedirect || defaultRedirectRoute;
                if (savedRedirect) sessionStorage.removeItem('auth_redirect_path');

                setTimeout(() => {
                  window.location.replace(destination);
                }, 2000);
              },
              onError: error => {
                console.error('Microsoft OAuth /auth/me error:', error);
                setStatus('error');
                setMessage('Failed to get user information');
                showToast.error('Authentication processing failed');

                setTimeout(() => {
                  navigate('/login', { replace: true });
                }, 3000);
              },
            }
          );
          return;
        } catch (error) {
          setStatus('error');
          setMessage('Failed to process Microsoft authentication response');
          showToast.error('Authentication processing failed');

          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }
      }

      // Handle traditional OAuth code flow (Google, Clio)
      if (!code || !state) {
        setStatus('error');
        setMessage('Invalid authentication response - missing required parameters');
        showToast.error('Authentication failed - invalid response');

        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
        return;
      }

      // Prevent duplicate submission across remounts/StrictMode by de-duping on state
      const dedupeKey = state ? `oauth_handled_${state}` : undefined;
      if (dedupeKey && sessionStorage.getItem(dedupeKey)) {
        return;
      }
      if (dedupeKey) sessionStorage.setItem(dedupeKey, '1');

      // Use the hook to handle the callback once (traditional OAuth flow)
      oauthCallbackMutation.mutate(
        { code, state },
        {
          onSuccess: async () => {
            setStatus('success');
            setMessage('Login successful! Redirecting...');
            const role = authApi.getCurrentUserRole();
            const defaultRedirectRoute = getDefaultRouterLoggedInCallback(
              currentPlatform as Platform,
              role as UserRole
            );
            // Redirect to saved path or default
            const savedRedirect = sessionStorage.getItem('auth_redirect_path');
            const destination = savedRedirect || defaultRedirectRoute;
            if (savedRedirect) sessionStorage.removeItem('auth_redirect_path');

            // Redirect
            setTimeout(() => {
              window.location.replace(destination);
            }, 2000);
          },
          onError: error => {
            console.error('OAuth callback error:', error);
            setStatus('error');
            setMessage(error instanceof Error ? error.message : 'Authentication failed');
            showToast.error('Login failed. Please try again.');

            // Redirect to login after delay
            setTimeout(() => {
              navigate('/login', { replace: true });
            }, 3000);
          },
        }
      );
    };

    handleCallback();
  }, []);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <Icon name="HiRefresh" size="2xl" className="text-xtnd-blue animate-spin" />;
      case 'success':
        return <Icon name="HiCheckCircle" size="2xl" className="text-green-500" />;
      case 'error':
        return <Icon name="HiXCircle" size="2xl" className="text-red-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-xtnd-blue';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-xtnd-blue to-xtnd-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4">
            <svg className="h-8 w-8 text-xtnd-blue" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7l-10-5z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {import.meta.env.VITE_APP_NAME || 'XTND'}
          </h1>
        </div>

        {/* Status Card */}
        <Card variant="default" className="shadow-xl">
          <CardBody className="text-center py-12">
            <div className="flex justify-center mb-6">{getStatusIcon()}</div>

            <h2 className={`text-xl font-semibold mb-4 ${getStatusColor()}`}>
              {status === 'processing' && 'Processing Login'}
              {status === 'success' && 'Login Successful'}
              {status === 'error' && 'Login Failed'}
            </h2>

            <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>

            {status === 'error' && (
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  You will be redirected to the login page shortly.
                </p>
                <button
                  onClick={() => navigate('/login', { replace: true })}
                  className="text-sm text-xtnd-blue hover:text-xtnd-dark underline"
                >
                  Go to login page now
                </button>
              </div>
            )}

            {status === 'processing' && (
              <div className="flex justify-center">
                <div className="animate-pulse flex space-x-1">
                  <div className="w-2 h-2 bg-xtnd-blue rounded-full"></div>
                  <div className="w-2 h-2 bg-xtnd-blue rounded-full animation-delay-200"></div>
                  <div className="w-2 h-2 bg-xtnd-blue rounded-full animation-delay-400"></div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
